import { Suspense, useEffect, useCallback } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { FourOhFourPageTemplate } from "@/templates/404/404-page/src/404-page.template";
import { UniformEntryPageTemplate } from "@/templates/uniform-entry/uniform-entry-page/src";
import { experiments, useExperimentExposure } from "@/utils/experiment";

export type UniformEntryPageProps = {
    composition: RootComponentInstance;
    compositionMetrics?: {
        compositionFetchStart?: number;
        compositionCacheStatus?: string;
        compositionFetchEnd?: number;
        ssrEndTime?: number;
    };
    isUniformPlayground?: boolean;
    isAllowPostMessage?: boolean;
};
export const UniformEntryPage = ({
    composition,
    compositionMetrics,
    isUniformPlayground,
    isAllowPostMessage
}: UniformEntryPageProps) => {
    const analyticsClient = useAnalyticsClient();
    const [dxpMigrationExposureFired, fireDxpMigrationExposure] =
        useExperimentExposure(experiments.dxpMigrationSoftwarePage);

    const { compositionFetchStart, compositionCacheStatus, ssrEndTime } =
        compositionMetrics || {};

    const sendPageRenderAnalyticsEvent = useCallback(
        (
            pageRenderTime: number,
            ssrEndTime: number,
            compositionFetchStart: number,
            fcp: number
        ) => {
            if (pageRenderTime && analyticsClient) {
                analyticsClient.sendUIEvent({
                    action: "pageRender",
                    actionSubject: "uniformPage",
                    source: window.location.pathname,
                    attributes: {
                        pageRenderTime: pageRenderTime,
                        compositionFetchStart,
                        ssrEndTime,
                        compositionCacheStatus,
                        fcp,
                        isUniformPlayground: isUniformPlayground || false
                    }
                });
            }
        },
        [analyticsClient, compositionCacheStatus, isUniformPlayground]
    );

    useEffect(() => {
        // @ts-ignore
        const measureFirstContentfulPaint = () => {
            try {
                // First, check if FCP is already available
                const paintEntries = performance.getEntriesByType("paint");
                const firstContentfulPaint = paintEntries.find(
                    (entry) => entry.name === "first-contentful-paint"
                );

                if (
                    firstContentfulPaint &&
                    ssrEndTime &&
                    compositionFetchStart
                ) {
                    const fcp = firstContentfulPaint.startTime;

                    // Calculate page render time
                    const pageRenderTime =
                        ssrEndTime - compositionFetchStart + fcp;

                    if (pageRenderTime) {
                        sendPageRenderAnalyticsEvent(
                            pageRenderTime,
                            ssrEndTime,
                            compositionFetchStart,
                            fcp
                        );
                    }

                    return firstContentfulPaint.startTime;
                }

                // If FCP isn't available yet, observe for it
                const observer = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const fcpEntry = entries.find(
                        (entry) => entry.name === "first-contentful-paint"
                    );

                    if (fcpEntry && compositionFetchStart && ssrEndTime) {
                        const fcp = fcpEntry.startTime;

                        // Calculate page render time
                        const pageRenderTime =
                            ssrEndTime - compositionFetchStart + fcp;

                        if (pageRenderTime) {
                            sendPageRenderAnalyticsEvent(
                                pageRenderTime,
                                ssrEndTime,
                                compositionFetchStart,
                                fcp
                            );
                        }

                        observer.disconnect();
                    }
                });

                observer.observe({ entryTypes: ["paint"] });

                // Cleanup observer after 10 seconds to prevent memory leaks
                setTimeout(() => {
                    observer.disconnect();
                }, 10000);

                return undefined;
            } catch (error) {
                console.warn("Error measuring Page Render Time:", error);
                return undefined;
            }
        };

        measureFirstContentfulPaint();
    }, [
        compositionFetchStart,
        compositionMetrics,
        sendPageRenderAnalyticsEvent,
        ssrEndTime
    ]);

    useEffect(() => {
        if (!dxpMigrationExposureFired) {
            fireDxpMigrationExposure();
        }
    }, [dxpMigrationExposureFired, fireDxpMigrationExposure]);

    return (
        <ContentNotFoundErrorBoundary fallback={<FourOhFourPageTemplate />}>
            <div data-testid="uniform-entry-page-root">
                <Suspense>
                    <UniformEntryPageTemplate
                        composition={composition}
                        isUniformPlayground={isUniformPlayground}
                        isAllowPostMessage={isAllowPostMessage}
                    />
                </Suspense>
            </div>
        </ContentNotFoundErrorBoundary>
    );
};
